import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // On mount, try to restore session via stored refresh token
  useEffect(() => {
    const restoreSession = async () => {
      const storedRefreshToken = localStorage.getItem('refreshToken');
      if (!storedRefreshToken) {
        setIsLoading(false);
        return;
      }
      try {
        const { data } = await api.post('/api/auth/refresh', { refreshToken: storedRefreshToken });
        window.__accessToken = data.accessToken;
        setUser(data.user);
        setOrganization(data.user?.organization || null);
      } catch {
        // No valid session
        localStorage.removeItem('refreshToken');
        window.__accessToken = null;
        setUser(null);
        setOrganization(null);
      } finally {
        setIsLoading(false);
      }
    };
    restoreSession();
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/api/auth/login', { email, password });
    window.__accessToken = data.accessToken;
    localStorage.setItem('refreshToken', data.refreshToken);
    setUser(data.user);
    setOrganization(data.user?.organization || null);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      await api.post('/api/auth/logout', { refreshToken });
    } catch {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('refreshToken');
      window.__accessToken = null;
      setUser(null);
      setOrganization(null);
      window.location.href = '/login';
    }
  }, []);

  const isAdmin = user?.role === 'ADMIN';
  const isManager = user?.role === 'MANAGER' || user?.role === 'ADMIN';

  return (
    <AuthContext.Provider value={{ user, organization, isAdmin, isManager, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
